import { Stepper, Title, Button } from '@components/ui';
import { customQuestionnaireStore, questionnaireStore } from '@stores';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { MouseEvent } from 'react';

import styles from './FormStep.module.scss';

export interface FormStepProps {
  title: string;
  description?: string;
  category?: string;
  totalSteps: number;
  children: React.ReactNode;
  button?: {
    label: string;
    url: string;
  };
  formId?: number;
  hideControls?: boolean;
  hideSteps?: boolean;
  hideModelAndUsecase?: boolean;
  hideDraft?: boolean;
  nextText?: string;
  showButton?: boolean;
  step?: number;
  maxStep?: number;
  lastConfirmedStep?: number;
  disabledNext?: boolean;
  loadingNext?: boolean;
  modelQuestionnaire?: boolean;
  customQuestionnaire?: boolean;
  lockNavigation?: boolean;
  onNext?: () => void;
  onBack?: (e: MouseEvent<HTMLButtonElement>) => void;
  onStepChange?: (step: number) => void | Promise<void>;
}

export const FormStep = observer(
  ({
    title,
    description,
    category,
    totalSteps,
    button,
    children,
    formId,
    hideControls = false,
    hideModelAndUsecase = false,
    hideDraft = false,
    showButton = false,
    step = 1,
    maxStep = 1,
    lastConfirmedStep,
    hideSteps = false,
    nextText,
    disabledNext = false,
    loadingNext = false,
    modelQuestionnaire = false,
    customQuestionnaire = false,
    lockNavigation = false,
    onNext,
    onBack,
    onStepChange,
  }: FormStepProps) => {
    const { questionnaireData } = questionnaireStore;
    const {
      questionnaireData: customQuestionnaireData,
      customQuestionnaire: questionnaire,
      questionnaireAnswerSets,
    } = customQuestionnaireStore;

    const { id: versionId, isDraft: versionIsDraft, updatedAt: versionUpdatedAt } = customQuestionnaireData || {};

    let badgeLabel: string;
    let badgeVariantClass: 'badgePrefilled' | 'badgeDraft' | 'badgeNew';
    let versionName: string;
    let versionMeta: string;

    if (versionIsDraft === true) {
      badgeLabel = 'Draft';
      badgeVariantClass = 'badgeDraft';
      versionName = `Draft Assessment – ${versionId}`;
      versionMeta = `Last saved ${dayjs(versionUpdatedAt).format('DD MMM YYYY, HH:mm')}`;
    } else if (versionId != null) {
      badgeLabel = 'Prefilled';
      badgeVariantClass = 'badgePrefilled';
      versionName = `Executed Assessment – ${versionId}`;
      versionMeta = `Answers from ${dayjs(versionUpdatedAt).format('DD MMM YYYY')} · saving creates a new version`;
    } else {
      badgeLabel = 'New';
      badgeVariantClass = 'badgeNew';
      versionName = 'New version';
      versionMeta = questionnaireAnswerSets?.length ? '' : 'No earlier versions for this use case';
    }

    const handleChangeVersion = () => {
      if (customQuestionnaireStore.hasUnsavedAnswers) {
        customQuestionnaireStore.setShowChangeVersionDialog(true);
      } else {
        customQuestionnaireStore.setShowAllCustomQuestionnaire(true);
      }
    };

    const headerBlocks = (
      <>
        {category && <div className={styles.category}>{category}</div>}
        <div
          className={styles.titleHeader}
          data-testid="form-step-title"
        >
          <Title htmlElement="h2">{title}</Title>
          {button && step === 1 && hideSteps && (
            <Button
              className={styles.button}
              view="animate"
              link={{ url_label: button.label, url: button.url }}
            />
          )}
        </div>
        {description && <div className={styles.description}>{description}</div>}
        {!hideModelAndUsecase && (
          <div
            className={clsx(styles.info, customQuestionnaire && styles.infoCustom)}
            style={{ marginTop: hideSteps && '16px', marginBottom: hideSteps && '-8px' }}
          >
            <div className={styles.item}>
              <div className={styles.subtitle}>Use case</div>
              <div className={styles.name}>
                {modelQuestionnaire && questionnaireData.useCaseName}
                {customQuestionnaire && (customQuestionnaireData?.useCaseName || '')}
              </div>
            </div>
            {/* show model and use case in data collection */}
            {modelQuestionnaire && (
              <div className={styles.item}>
                <div className={styles.subtitle}>Model</div>
                <div className={styles.name}>{questionnaireData.modelName}</div>
              </div>
            )}
            {customQuestionnaire && !hideSteps && (
              <div className={styles.item}>
                <div className={styles.subtitle}>Questionnaire</div>
                <div className={styles.name}>{questionnaire?.name}</div>
              </div>
            )}
            {customQuestionnaire && !hideSteps && (
              <div className={styles.versionItem}>
                <div className={styles.subtitle}>Version</div>
                <div className={styles.versionRow}>
                  <span className={clsx(styles.badge, styles[badgeVariantClass])}>{badgeLabel}</span>
                  <span className={styles.name}>{versionName}</span>
                </div>
                {versionMeta && <div className={styles.versionMeta}>{versionMeta}</div>}
                <button
                  type="button"
                  className={styles.changeVersion}
                  onClick={handleChangeVersion}
                >
                  Change version
                </button>
              </div>
            )}
          </div>
        )}
      </>
    );

    const stepperRow = (
      <div className={styles.header}>
        {!hideSteps && (
          <Stepper
            steps={totalSteps}
            currentStep={step}
            maxStep={maxStep}
            lastConfirmedStep={lastConfirmedStep}
            onStepClick={onStepChange}
            customQuestionnaire={customQuestionnaire}
          />
        )}
        {button && step === 1 && !hideSteps && showButton && (
          <Button
            className={styles.button}
            view="animate"
            link={{ url_label: button.label, url: button.url }}
          />
        )}
      </div>
    );

    return (
      <div
        className={clsx(styles.wrapper, customQuestionnaire && styles.customQuestionnaireWrapper)}
        style={{ gap: hideSteps ? '0px' : '8px' }}
      >
        {customQuestionnaire ? (
          <div className={styles.headerCard}>
            {headerBlocks}
            <div className={styles.headerCardDivider} />
            {stepperRow}
          </div>
        ) : (
          <>
            {headerBlocks}
            {stepperRow}
          </>
        )}
        {children}
        <div className={styles.controls}>
          {!hideControls && (
            <Button
              form={`form-id-${formId}`}
              id="back"
              view="secondary"
              onClick={onBack}
              type={onBack ? 'button' : 'submit'}
              disabled={lockNavigation}
            >
              Back
            </Button>
          )}
          {step < totalSteps && !hideControls && (
            <Button
              form={`form-id-${formId}`}
              id="next"
              onClick={onNext}
              disabled={disabledNext || lockNavigation}
              loading={loadingNext}
            >
              {nextText || 'Next'}
            </Button>
          )}
          {step === totalSteps && !hideControls && (
            <Button
              id="execute"
              form={`form-id-${formId}`}
              onClick={onNext}
              disabled={disabledNext || lockNavigation}
              loading={loadingNext}
            >
              {nextText || (customQuestionnaire ? 'Submit' : 'Execute')}
            </Button>
          )}
          {(modelQuestionnaire || customQuestionnaire) && !hideDraft && (
            <Button
              form={`form-id-${formId}`}
              id="draft"
              disabled={loadingNext || lockNavigation}
            >
              Save as draft
            </Button>
          )}
        </div>
      </div>
    );
  },
);
